import "setimmediate";
import DataLoader from "dataloader";
import { each, find, map, uniqueId } from "lodash";
import { Evt } from "@tootools/event";
import { cleanApiClause } from "@radarly/models/clause/api";
import { SearchData, SearchParams } from "@radarly/models/search";
import { RadarlyAPI } from "./base";

type Query = {
  id: string;
} & SearchParams;

function getProjectRequests(requests: readonly [number, Query][]) {
  const _requests: {
    requests: Query[];
    project: number;
  }[] = [];
  each(requests, ([project, sp], i) => {
    const existingRequest = find(_requests, {
      project,
    });
    if (!existingRequest) {
      _requests.push({
        project,
        requests: [sp],
      });
    } else {
      existingRequest.requests.push(sp);
    }
  });
  return _requests;
}

export class SearchAPI extends RadarlyAPI {
  private batchLoader: DataLoader<[number, Query], SearchData>;
  private pendingEvents: Record<string, Evt> = {};
  private eventTarget = new Evt();
  constructor() {
    super();
    this.batchLoader = new DataLoader(
      async (keys) => {
        const requests = getProjectRequests(keys);
        await Promise.all(
          map(requests, async ({ project, requests }) => {
            const onStreamChunk = (event: MessageEvent) => {
              if (this.pendingEvents[event.data.id]) {
                if (event.data.error) {
                  this.pendingEvents[event.data.id].emit(
                    new CustomEvent("/error", { detail: event.data.error })
                  );
                } else {
                  this.pendingEvents[event.data.id].emit(
                    new MessageEvent("/data", {
                      data: event.data,
                    })
                  );
                }
              }
            };
            const onError = (event: CustomEvent) => {
              each(requests, ({ id }) => {
                if (this.pendingEvents[id]) {
                  this.pendingEvents[id].emit(
                    new CustomEvent("/error", { detail: event.detail })
                  );
                }
              });
            };
            this.eventTarget.attach("/data", onStreamChunk);
            this.eventTarget.attach("/error", onError);
            await this.stream(
              this.eventTarget,
              `/projects/${project}/msearch`,
              {
                requests,
              }
            );
            this.eventTarget.detach("/data", onStreamChunk);
            this.eventTarget.detach("/error", onError);
          })
        );
        return map(keys, ([_, sp]) => null);
      },
      { cache: false, maxBatchSize: 20 }
    );
  }
  async search<T extends SearchData>(
    project: number,
    query: SearchParams,
    mutualized?: boolean
  ) {
    const validQuery: SearchParams = {
      ...query,
      query: cleanApiClause(query.query),
    };
    if (mutualized) {
      const id = uniqueId("query_");
      this.pendingEvents[id] = new Evt();
      return new Promise<T>((resolve, reject) => {
        this.pendingEvents[id].attach("/data", (event: MessageEvent) => {
          resolve(event.data);
        });
        this.pendingEvents[id].attach("/error", (event: CustomEvent) => {
          reject(event.detail);
        });
        this.batchLoader.load([project, { ...validQuery, id }]);
      }).finally(() => {
        this.pendingEvents[id].clear("/data");
        this.pendingEvents[id].clear("/error");
        delete this.pendingEvents[id];
      });
    } else {
      return await this.post<T>(`/projects/${project}/search`, validQuery);
    }
  }
}
